import type { BlogPost } from "../blogTypes";

export const fixedVsIndefiniteRentalContractNetherlands: BlogPost = {
    slug: "fixed-vs-indefinite-rental-contract-netherlands",
    title: "Fixed vs Indefinite Rental Contract in the Netherlands: What's Better?",
    metaTitle: "Fixed vs Indefinite Rental Contract Netherlands | Comparison",
    metaDescription: "Since 1 July 2024, indefinite contracts are the Dutch default. See when a temporary contract can still be used, and how termination rules differ in 2026.",
    excerpt: "Since 1 July 2024, indefinite contracts are the norm in the Netherlands and ordinary temporary contracts are limited to specific exceptions. Here's what that means for your rights.",
    date: "2026-02-05",
    updated: "2026-08-23",
    readTime: "4 min read",
    category: "Rental Law",
    keywords: ["fixed term rental Netherlands", "indefinite rental contract Dutch", "tijdelijk huurcontract", "huurcontract bepaalde tijd"],
    content: `
## The Two Main Contract Types

When renting in the Netherlands, you'll encounter two main types of contracts:

1. **Fixed-term contract** (bepaalde tijd) — has a specific end date
2. **Indefinite contract** (onbepaalde tijd) — no end date, continues until terminated

Since [1 July 2024](https://www.volkshuisvestingnederland.nl/onderwerpen/huren-en-wonen/huurbescherming-en-huurcontracten/vaste-huurcontracten-de-norm), the law has changed which of these a landlord can normally offer. This is a legal rule, not just typical practice, so it matters which regime applies to your contract.

## Indefinite Is Now the Default

Since 1 July 2024, a new residential tenant normally receives an indefinite contract. A landlord may use an ordinary temporary contract of up to two years only for specifically designated groups and situations described in [government guidance on temporary contracts](https://www.rijksoverheid.nl/vraag-en-antwoord/huurwoning-zoeken/wanneer-kan-ik-tijdelijk-huren). Other special regimes remain possible, including interim letting with a valid return clause, target-group contracts, vacancy-law letting, hospita letting and genuinely short-stay use. Each of these has different termination rules, so the label on your contract is not enough — you need to identify which regime actually applies.

## Ordinary Temporary Contract Under an Exception

Where the statutory exception applies:

- The contract has a stated end date and may last no more than **two years**.
- The landlord must notify the tenant in writing between **one and three months** before the end date.
- **The tenant may terminate early**, subject to the applicable notice period.
- **The landlord cannot terminate this ordinary temporary contract early.**
- If the contract is continued past the end date, it automatically becomes indefinite.

## Contracts Made Before 1 July 2024

Transitional rules preserve qualifying old temporary contracts: up to two years for independent housing and up to five years for a room or other non-independent housing. These older limits are **not** the general rule for new contracts signed in 2026 — they only apply to qualifying contracts made before the 1 July 2024 change.

## Indefinite Contract (Onbepaalde Tijd)

### Key Features:
- No end date — the contract continues until properly terminated
- The tenant's notice period normally equals the interval between rent payments. It is usually one month and cannot exceed three months. A contract cannot simply impose three months when the rent is paid monthly.
- The landlord can **only terminate for a statutory ground** (for example serious tenant breach, urgent own use, or refusal of a reasonable new-tenancy offer)
- If the tenant does not agree to end the tenancy, only a court can end it
- Rent increases are regulated

### Pros for Tenants:
- Strong legal protection
- You can stay as long as you want and comply with the contract
- The landlord cannot simply choose not to renew
- You control when to leave, subject to notice

### Cons for Tenants:

## Which Is Better for Expats?

For most expats, an **indefinite contract** offers more security because:

- You don't always know how long you'll stay in the Netherlands
- You get stronger legal protection against termination
- When rent is paid monthly, the tenant can normally leave with one month's notice, subject to the rules for the particular contract type
- A landlord needs a statutory ground to end it

That said, if you know you only need housing for a short, defined period, a temporary contract under one of the statutory exceptions (or a genuinely short-stay arrangement) may fit better — as long as the exception genuinely applies to your situation.

## What to Watch Out For

- **A temporary-contract label that doesn't fit an exception.** Since 1 July 2024, an ordinary temporary contract is only lawful under specific statutory situations — not as a general default.
- **"Model contract" language** — some landlords use contracts that look standard but contain unusual termination clauses.
- **Return clauses.** A tenant-relocation clause (allowing the tenant to leave early, for example after a work transfer) is different from a landlord-return clause used in a valid interim lease (tussenhuur), where the landlord or a previous occupant returns after an agreed period. Neither is a general licence for a landlord to end any lease on short notice, and you should check the exact trigger and notice period rather than assume it overrides your normal protection.

## Check Your Contract Type

Not sure what type of contract you have? [Upload it to RentSense NL](/app) for an analysis developed with reference to current official Dutch government and Huurcommissie sources. We'll help identify your contract type and explain what it means for your rights — though for a specific dispute, you should also consult the Juridisch Loket or a huurrecht specialist.

## Sources checked

- [Government guidance on temporary contracts](https://www.rijksoverheid.nl/vraag-en-antwoord/huurwoning-zoeken/wanneer-kan-ik-tijdelijk-huren)
- [Volkshuisvesting Nederland: indefinite contracts as the norm](https://www.volkshuisvestingnederland.nl/onderwerpen/huren-en-wonen/huurbescherming-en-huurcontracten/vaste-huurcontracten-de-norm)
    `,
};
